import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Shared top-scrollbar + overflow detection for wide tables.
 *
 * Mounts a synthetic top scrollbar above the table that mirrors the bottom
 * scrollbar's scrollLeft and only reports `hasOverflow=true` when the table's
 * `scrollWidth` exceeds its viewport. Consumers wire the returned refs to the
 * top track wrapper, the (1px-tall) width-spacer inside it, and the bottom
 * table scroll container respectively.
 *
 * Notes (preserved from F21-004):
 * - `setHasOverflow` uses a functional update that skips identical values so
 *   resize ticks don't schedule needless effect reruns.
 * - Scroll listeners are attached unconditionally because gating on
 *   `hasOverflow` introduced a one-frame race where the top scrollbar was
 *   visible but unsynced; syncing `scrollLeft` while not overflowing is a
 *   cheap no-op.
 */
export function useTopScrollbarSync(deps: ReadonlyArray<unknown>): {
  topScrollRef: RefObject<HTMLDivElement>;
  bottomScrollRef: RefObject<HTMLDivElement>;
  topScrollContentRef: RefObject<HTMLDivElement>;
  hasOverflow: boolean;
} {
  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);
  const topScrollContentRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const topScroll = topScrollRef.current;
    const bottomScroll = bottomScrollRef.current;
    const topScrollContent = topScrollContentRef.current;

    if (!bottomScroll) return;

    const syncTopToBottom = () => {
      if (bottomScroll && topScroll) {
        bottomScroll.scrollLeft = topScroll.scrollLeft;
      }
    };

    const syncBottomToTop = () => {
      if (topScroll && bottomScroll) {
        topScroll.scrollLeft = bottomScroll.scrollLeft;
      }
    };

    const updateScrollWidth = () => {
      const table = bottomScroll.querySelector('table');
      if (table) {
        const isOverflowing = table.scrollWidth > bottomScroll.clientWidth;
        setHasOverflow((prev) => (prev === isOverflowing ? prev : isOverflowing));

        if (topScrollContent) {
          topScrollContent.style.width = `${table.scrollWidth}px`;
        }
      }
    };

    updateScrollWidth();
    const resizeObserver = new ResizeObserver(updateScrollWidth);
    resizeObserver.observe(bottomScroll);

    if (topScroll) {
      topScroll.addEventListener('scroll', syncTopToBottom);
    }
    bottomScroll.addEventListener('scroll', syncBottomToTop);

    return () => {
      if (topScroll) {
        topScroll.removeEventListener('scroll', syncTopToBottom);
      }
      bottomScroll.removeEventListener('scroll', syncBottomToTop);
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { topScrollRef, bottomScrollRef, topScrollContentRef, hasOverflow };
}
